/* eslint-disable no-plusplus */
/* eslint-disable no-await-in-loop */
import { v4 as uuidv4 } from 'uuid';
import isEmail from 'validator/es/lib/isEmail';
import axios from 'axios';
import moment from 'moment';
import { CURRENCIES } from 'constants/sessions';

const calendarWeekdays: { key: string, value: string }[] = [
  { key: 'Monday', value: 'MO' },
  { key: 'Tuesday', value: 'TU' },
  { key: 'Wednesday', value: 'WE' },
  { key: 'Thursday', value: 'TH' },
  { key: 'Friday', value: 'FR' },
  { key: 'Saturday', value: 'SA' },
  { key: 'Sunday', value: 'SU' },
];

export function getCurrentTenant() {
  const currentDomain = window.location.hostname === 'localhost' ? '1014lasttest' : window.location.hostname;
  return currentDomain.split('.')[0];
}

export function getUserHubUrl(replace?: string) {
  if (window.location.hostname === 'localhost') return 'http://localhost:3000';
  const currentDomain = window.location.origin;
  return currentDomain.replace('.expert', replace ?? '');
}

// eslint-disable-next-line no-useless-escape
export const isEmailValid = (val: string): boolean => isEmail(val);

export const isNumeric = (val: string): boolean => /^-?\d+$/.test(val);

export const uniqueIdGenerate = () => uuidv4();

export const replaceExt = (url: string, ext: string) => `${url.substring(0, url.lastIndexOf('.'))}${ext}`;

// eslint-disable-next-line no-promise-executor-return
export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// eslint-disable-next-line consistent-return
export const isStreamableVideoAvailable = async (url: string): Promise<any> => {
  for (let index = 0; index < 60; index++) {
    // checking max 5 min
    try {
      await sleep(5000);
      const streamUrl = replaceExt(url, '.m3u8');
      return await axios.head(streamUrl);
    } catch (error) {
      // console.log(error);
    }
  }
};

export const getCurrncySymbol = (currency: string | undefined) => CURRENCIES[(currency ?? 'USD') as keyof typeof CURRENCIES];

export const enumerateDaysBetweenDates = async (
  startDate: moment.MomentInput,
  endDate: moment.MomentInput,
): Promise<string[]> => {
  const dates = [];
  const currDate = moment(startDate).startOf('day');
  const lastDate = moment(endDate).startOf('day');
  for (let m = moment(currDate); m.isSameOrBefore(lastDate); m.add(1, 'days')) {
    dates.push(m.toISOString());
  }
  return dates;
};

export const getDateWithTime = (time: string, date: string) => {
  // split the expert avalible time and append the avalible time
  // to the date string to get the date with the time

  // example
  // if the day is 2021-01-02 and the given time time is 9
  // this will return 2021-01-02:9.00

  const t = time.split(':').map((item) => parseInt(item, 10));
  const m = moment(date);
  m.set({
    hour: t[0],
    minute: t[1],
    second: t[2],
    millisecond: 0,
  });
  return m.toISOString();
};

export const getTimeStops = (start: string, end: string, min: number): any[] => {
  // generate the time slots based on the given time in minutes
  // by using the startTime and the endTime generate the time slots
  const startTime = moment(start);
  const endTime = moment(end);

  const timeStops: any = [];

  if (endTime.isBefore(startTime)) {
    return timeStops;
  }

  while (startTime < endTime) {
    timeStops.push({
      start: startTime.toDate().toISOString(),
      end: startTime.add(min, 'minutes').toDate().toISOString(),
    });
  }
  return timeStops;
};

export const removeTags = (str: string) => {
  if ((str === null) || (str === '')) { return ''; }

  return str
    .replace(/(<([^>]+)>)/ig, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with regular spaces
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .trim();
};

export const truncateString = (text: string, num: number) => {
  if (text.length > num) {
    return `${text.slice(0, num)}...`;
  }
  return text;
};

export const getAvatarLetters = (user: any) => {
  if (user?.firstName && user?.lastName) {
    return `${removeTags(user.firstName).substring(0, 1)}${removeTags(user.lastName).substring(0, 1)}`.toUpperCase();
  }
  if (user?.firstName) {
    return `${removeTags(user.firstName).substring(0, 2)}`.toUpperCase();
  }
  if (user?.lastName) {
    return `${removeTags(user.lastName).substring(0, 2)}`.toUpperCase();
  }
  if (user?.email) {
    return `${removeTags(user.email).substring(0, 2)}`.toUpperCase();
  }
  return 'NA';
};

export const getUserName = (user: any) => {
  if (user?.firstName && user?.lastName) {
    return `${removeTags(user.firstName)} ${removeTags(user.lastName)}`;
  }
  if (user?.firstName) {
    return `${removeTags(user.firstName)}`;
  }
  if (user?.lastName) {
    return `${removeTags(user.lastName)}`;
  }
  if (user?.email) {
    return `${removeTags(user.email)}`;
  }
  return 'NA';
};

export const getCalendarConfig = async (session: any, slot: any, entName: string) => {
  const baseUrl = window.location.origin.toString();
  const config: any = {
    title: `${entName}: ${session.title}`,
    location: '',
    description: `For full details or to join the session, visit the below link.<br><a href="${baseUrl}/sessions/summary/${session.id}">Click here</a>.`,
    start: new Date(),
    end: new Date(),
  };
  config.start = moment(slot.from).toDate();
  config.end = moment(slot.to).toDate();

  return config;
};

export const retry = async (
  fn: () => Promise<void>,
  retries: number,
  delay: number,
): Promise<void> => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });
      return retry(fn, retries - 1, delay);
    }

    throw new Error('Failed to complete after multiple attempts');
  }
};

export const getVideoThumbnailUrl = (url: string) => {
  if (!url) {
    return '';
  }

  const mediaUrl = url.slice(0, url.lastIndexOf('.'));
  return `${mediaUrl}.0000001.jpg`;
};

export const getVideoPosterUrl = async (url: string) => {
  if (!url) {
    return '';
  }

  const mediaUrl = url.slice(0, url.lastIndexOf('.'));

  try {
    await axios.head(`${mediaUrl}_poster.0000001.jpg`);
    return `${mediaUrl}_poster.0000001.jpg`;
  } catch (error) {
    // Handle error silently
  }

  try {
    await axios.head(`${mediaUrl}.0000001.jpg`);
    return `${mediaUrl}_poster.0000001.jpg`;
  } catch (error) {
    // Handle error silently
  }

  return undefined;
};

export const isValidLink = (url: string) => {
  if (!url) return false;
  // eslint-disable-next-line no-useless-escape
  const urlRegMatch = /^((https?):\/\/)?(www\.)?[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*(\.[a-zA-Z]{2,})(\/[^\s]*)?$/;
  return urlRegMatch.test(url);
};

export const openInNewTab = (href: string) => {
  Object.assign(document.createElement('a'), {
    target: '_blank',
    rel: 'noopener noreferrer',
    href,
  }).click();
};
